import { InfoCard } from '@/components/info-card';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getSchool, getStudentGroup } from '@/lib/api-calls';
import { getCoachOverallGradeLabel } from '@/lib/coach-utils';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { formatStudentGrades } from '@/lib/student-group-utils';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list';
import { StudentGroupStatusBadge } from '@/routes/_layout/scheduling/student-groups/-components/student-group-status-badge';
import { EmailsSentBadge } from '@/routes/_layout/teachers/-components/emails-sent-badge';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { adjustMinimalClassTimeAcrossTZ } from '@airreading/common/data';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit } from 'lucide-react';
import { CreateEditOneTimeClass } from '../../-components/create-edit-one-time-class';
import { schoolDetailsTabs } from '../../-components/tabs';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/one-time-classes/$classId')({
  component: SchoolOneTimeClassDetailPage,
});

function SchoolOneTimeClassDetailPage() {
  const { schoolId } = Route.useParams();

  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load class detail. Please try again.',
          back: {
            backLink: {
              to: '/orgs/schools/$schoolId/one-time-classes',
              params: { schoolId },
            },
          },
        };
      }}
    >
      <SchoolOneTimeClassDetail schoolId={schoolId} />
    </InfoPageSuspenseBoundary>
  );
}

function SchoolOneTimeClassDetail({ schoolId }: { schoolId: string }) {
  const { classId } = Route.useParams();

  const { data } = useSuspenseQuery(getSchool(schoolId));
  const { data: classData } = useSuspenseQuery(getStudentGroup(classId));
  const {
    coach,
    students,
    grades,
    classTime,
    futureSessions,
    startingLesson,
    nextLesson,
    overrideStartPlainDate,
    emailsSent,
  } = classData;
  const schoolTimeZone = data.org.timeZone;
  const { schedule } = classTime;

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
          {
            label: data.name,
            link: {
              to: '/orgs/schools/$schoolId',
              params: { schoolId },
            },
          },
        ],
        currentPage: 'One-Time Class Details',
      }}
      tabs={schoolDetailsTabs({ schoolId }, 'one-time-classes')}
    >
      <InfoCard
        headerText="One-Time Class Details"
        headerActions={
          <CreateEditOneTimeClass
            trigger={<Button icon={Edit}>Edit</Button>}
            schoolId={schoolId}
            from={classData}
            schoolTimeZone={schoolTimeZone}
          />
        }
      >
        <InfoKeyValue
          label="Teacher"
          value={coach ? <Badge variant={'outline'}>{coach.fullNameOrEmail}</Badge> : <div>No teacher assigned</div>}
        />
        <InfoKeyValue label="Students" value={<PeopleBadgeList people={students} wrap={true} />} />
        <InfoKeyValue label="Grades" value={formatStudentGrades(grades)} />
        <InfoKeyValue label="Status" value={<StudentGroupStatusBadge studentGroupLike={classData} />} />
        <InfoKeyValue label="Schedule" value={<Badge variant={'outline'}>{schedule.name}</Badge>} />
        <InfoKeyValue label="Class date" value={formatPlainDate(overrideStartPlainDate || schedule.startPlainDate)} />
        <InfoKeyValue
          label={<span>Class time (CT)</span>}
          value={
            <Badge variant={'outline'}>
              {formatStudentClassTimeAbbr(
                adjustMinimalClassTimeAcrossTZ(classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
              )}
            </Badge>
          }
        />
        <InfoKeyValue label="Number of future sessions" value={futureSessions} />
        <InfoKeyValue label="Starting lesson" value={startingLesson.name} />
        <InfoKeyValue label="Next lesson" value={nextLesson ? nextLesson.name : '-'} />
        <InfoKeyValue label="Overall grade" value={getCoachOverallGradeLabel(coach?.coachProfile.overallGrade)} />
        <InfoKeyValue
          label="Emails sent"
          value={
            emailsSent?.length ? (
              <div className="flex items-center gap-2">
                {emailsSent.map((email) => (
                  <EmailsSentBadge key={email} emailsSent={email} />
                ))}
              </div>
            ) : (
              '-'
            )
          }
        />
      </InfoCard>
    </InfoPage>
  );
}
